import React, { useState, useRef } from "react";
import { Container, Modal, Button, ListGroup } from "react-bootstrap";
import { getSchoolIconPath, useScrollRestore } from "./utilities";


export function AlphabeticalSpellsList({stateKey, spells, onSpellClick, height, grayedOut}) {
    const [showIndex, setShowIndex] = useState(false);
    const containerRef = useRef(null);
    const letterRefs = useRef({});

    const scrollStateKey = `${stateKey}`
    useScrollRestore(containerRef, scrollStateKey);

    const scrollToLetter = (l) => {
        const header = letterRefs.current[l];
        if (!header) return;
        const nextItem = header.nextElementSibling;
        if (nextItem) {
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            const headerRect = header.getBoundingClientRect();
            const nextItemRect = nextItem.getBoundingClientRect();
            const scrollTop = container.scrollTop + (nextItemRect.top - containerRect.top - headerRect.height);
            container.scrollTo({
                top: scrollTop,
                behavior: "smooth",
            });
        }
        setShowIndex(false);
    };
    
    const openIndex = () => setShowIndex(true);
    const closeIndex = () => setShowIndex(false);
    
    spells.sort((a, b) => a.name.localeCompare(b.name));
    const grouped = spells.reduce((acc, spell) => {
        const letter = spell.name[0].toUpperCase();
        if (!acc[letter]) acc[letter] = [];
        acc[letter].push(spell);
        return acc;
    }, {});
    const letters = Object.keys(grouped).sort();
    
    return (
        <Container ref={containerRef} fluid style={{ height: height, overflowY: "auto" }}>
            <ListGroup
            as="ul"
            variant="flush"
            className="shadow-sm bg-white rounded mt-3 mb-3">
                {letters.map((letter) => (
                    <React.Fragment key={letter}>
                        <ListGroup.Item
                        variant={grayedOut === true ? "dark" : "secondary"}
                        className="fw-bold text-uppercase text-center sticky-top"
                        role="button"
                        tabIndex={0}
                        ref={(node) => {
                            if (node) letterRefs.current[letter] = node;
                        }}
                        onClick={openIndex}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openIndex()}>
                            {letter}
                        </ListGroup.Item>
                        {grouped[letter].map((spell) => (
                            <ListGroup.Item
                            as="li"
                            variant={grayedOut === true ? "secondary" : "light"}
                            key={spell.id}
                            action
                            onClick={() => onSpellClick(spell.id)}
                            className="d-flex align-items-center gap-3 py-3"
                            role="button"
                            aria-label={`Open ${spell.name}`}>
                                <span aria-hidden="true" className="fs-5 d-inline-flex">
                                    <img src={getSchoolIconPath(spell?.school?.name)} alt="" style={{width:"42px",height:"42px"}}/>
                                </span>
                                <span className="fw-medium">
                                    <div className="fs-6 fw-bold">
                                        {spell.name}
                                    </div>
                                    <small className="fs-7 text-muted">
                                        {spell.brief}
                                    </small>
                                </span>
                            </ListGroup.Item>
                        ))}
                    </React.Fragment>
                ))}
            </ListGroup>
            <Modal show={showIndex} onHide={closeIndex} centered restoreFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Vai alla lettera</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap">
                        {letters.map((l) => (
                            <Button
                                key={l}
                                variant="light"
                                className="m-1 border"
                                onClick={() => scrollToLetter(l)}>
                                {l}
                            </Button>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );
}


export function ClassLevelOrderedSpellsList({stateKey, spells, classId, onSpellClick, height}) {
    const [showIndex, setShowIndex] = useState(false);
    const containerRef = useRef(null);
    const levelRefs = useRef({});
    
    const scrollStateKey = `${stateKey}`
    useScrollRestore(containerRef, scrollStateKey);

    const scrollToLevel = (l) => {
        const header = levelRefs.current[l];
        if (!header) return;
        const nextItem = header.nextElementSibling;
        if (nextItem) {
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            const headerRect = header.getBoundingClientRect();
            const nextItemRect = nextItem.getBoundingClientRect();
            const scrollTop = container.scrollTop + (nextItemRect.top - containerRect.top - headerRect.height);
            container.scrollTo({
                top: scrollTop,
                behavior: "smooth",
            });
        }
        setShowIndex(false);
    };

    const openIndex = () => setShowIndex(true);
    const closeIndex = () => setShowIndex(false);
    
    spells = spells.map((s) => ({
        id: s.id,
        name: s.name,
        brief: s.brief,
        school: s.school,
        subSchool: s.subSchool,
        casterLevel: s.casterLevels.find(i => i.class.id === classId)?.level ?? null
    })).filter((s) => s.casterLevel !== null);
    spells.sort((a, b) => {
        if (a.casterLevel !== b.casterLevel) return a.casterLevel - b.casterLevel;
        return a.name.localeCompare(b.name);
    });
    const grouped = spells.reduce((acc, spell) => {
        const level = spell.casterLevel;
        if (!acc[level]) acc[level] = [];
        acc[level].push(spell);
        return acc;
    }, {});
    const levels = Object.keys(grouped).sort();

    return (
        <Container ref={containerRef} fluid style={{height: height, overflowY: "auto"}}>
            <ListGroup
            as="ul"
            variant="flush"
            className="shadow-sm bg-white rounded mt-3 mb-3">
                {levels.map((lvl) => (
                    <React.Fragment key={lvl}>
                        <ListGroup.Item
                        variant="secondary"
                        className="fw-bold text-uppercase text-center sticky-top"
                        role="button"
                        tabIndex={0}
                        ref={(node) => {
                            if (node) levelRefs.current[lvl] = node;
                        }}
                        onClick={openIndex}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openIndex()}>
                            {`${lvl}`}
                        </ListGroup.Item>
                        {grouped[lvl].map((spell) => (
                            <ListGroup.Item
                            as="li"
                            key={spell.id}
                            action
                            onClick={() => onSpellClick(spell.id)}
                            className="d-flex align-items-center gap-3 py-3"
                            role="button"
                            aria-label={`Open ${spell.name}`}>
                                <span aria-hidden="true" className="fs-5 d-inline-flex">
                                    <img src={getSchoolIconPath(spell?.school?.name)} alt="" style={{width:"42px",height:"42px"}}/>
                                </span>
                                <span className="fw-medium">
                                    <div className="fs-6 fw-bold">
                                        {spell.name}
                                    </div>
                                    <small className="fs-7 text-muted">
                                        {spell.brief}
                                    </small>
                                </span>
                            </ListGroup.Item>
                        ))}
                    </React.Fragment>
                ))}
            </ListGroup>
            <Modal show={showIndex} onHide={closeIndex} centered restoreFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Vai al livello</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap">
                        {levels.map((l) => (
                            <Button
                                key={l}
                                variant="light"
                                className="m-1 border"
                                onClick={() => scrollToLevel(l)}>
                                {l}
                            </Button>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );
}
